import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageCircle, Send, Pin, PinOff, MoreVertical, Edit2, Trash2,
  AtSign, Bell, Lock, Globe, AlertTriangle, RefreshCw, Crown,
  UserPlus, Check, CheckCircle, XCircle, Copy, Link2, BellRing, BellOff
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * CommentsPanel - Reusable comment panel for any entity
 * Tier-gated features: @mentions (Enterprise), pinning (Business+), notifications (Enterprise)
 */
const CommentsPanel = ({ 
  entityType, 
  entityId, 
  tenantId,
  onCommentAdded,
  compact = false 
}) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [visibility, setVisibility] = useState('org');
  const [assignedTo, setAssignedTo] = useState(null);
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const textareaRef = useRef(null);
  const mentionRef = useRef(null);

  useEffect(() => {
    loadComments();
    loadCapabilities();
    loadSubscription();
  }, [entityType, entityId, tenantId]);

  const loadCapabilities = async () => {
    try {
      const res = await api.getCommentCapabilities(tenantId, user?.id);
      setCapabilities(res.data);
    } catch (err) {
      console.error('Failed to load comment capabilities:', err);
    }
  };

  const loadSubscription = async () => {
    if (!entityType || !entityId || !tenantId || !user?.id) return;
    try {
      const res = await api.getCommentSubscription({
        entity_type: entityType,
        entity_id: entityId,
        tenant_id: tenantId,
        user_id: user.id
      });
      setIsSubscribed(res.data.subscribed);
    } catch (err) {
      // Silently fail
    }
  };

  const handleToggleSubscription = async () => {
    if (!entityType || !entityId || !tenantId || !user?.id) return;
    try {
      if (isSubscribed) {
        await api.unsubscribeFromComments({
          entity_type: entityType,
          entity_id: entityId,
          tenant_id: tenantId,
          user_id: user.id
        });
        setIsSubscribed(false);
      } else {
        await api.subscribeToComments({
          entity_type: entityType,
          entity_id: entityId,
          tenant_id: tenantId,
          user_id: user.id,
          user_name: user.name || user.email || 'User'
        });
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error('Failed to toggle subscription:', err);
    }
  };

  const loadComments = async () => {
    if (!entityType || !entityId || !tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getComments({
        entity_type: entityType,
        entity_id: entityId,
        tenant_id: tenantId,
        user_id: user?.id || ''
      });
      setComments(res.data.comments || []);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.createComment({
        entity_type: entityType,
        entity_id: entityId,
        message: newComment.trim(),
        mentions: selectedMentions.map(u => u.id),
        visibility,
        pinned: false,
        assigned_to: assignedTo?.id || null,
        tenant_id: tenantId,
        user_id: user?.id || '',
        user_name: user?.name || user?.email || 'User',
        user_role: user?.roles?.[0]?.role_key || null
      });
      setNewComment('');
      setSelectedMentions([]);
      setAssignedTo(null);
      loadComments();
      if (onCommentAdded) onCommentAdded();
    } catch (err) {
      console.error('Failed to create comment:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && (detail.error === 'tier_locked' || detail.error === 'role_locked')) {
        setError(detail.message);
      } else {
        setError('Failed to post comment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId) => {
    if (!editText.trim()) return;
    try {
      await api.updateComment(commentId, {
        body: { message: editText.trim() },
        params: { tenant_id: tenantId, user_id: user?.id || '' }
      });
      setEditingId(null);
      setEditText('');
      loadComments();
    } catch (err) {
      console.error('Failed to update comment:', err);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.deleteComment(commentId, {
        tenant_id: tenantId,
        user_id: user?.id || ''
      });
      loadComments();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleTogglePin = async (comment) => {
    try {
      await api.updateComment(comment.id, {
        body: { pinned: !comment.pinned },
        params: { tenant_id: tenantId, user_id: user?.id || '' }
      });
      loadComments();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        alert(detail);
      }
    }
  };

  const handleNotify = async (commentId, channel) => {
    try {
      const res = await api.sendCommentNotification(commentId, {
        tenant_id: tenantId,
        user_id: user?.id || '',
        channel
      });
      if (res.data.sent) {
        alert(`Notification sent via ${channel}`);
      } else {
        alert(res.data.message || 'Notification not sent');
      }
    } catch (err) {
      console.error('Failed to send notification:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail.error === 'feature_locked') {
        alert(detail.message);
      }
    }
  };

  const handleToggleResolved = async (comment) => {
    try {
      await api.updateComment(comment.id, {
        body: { resolved: !comment.resolved },
        params: { tenant_id: tenantId, user_id: user?.id || '' }
      });
      loadComments();
    } catch (err) {
      console.error('Failed to toggle resolved:', err);
    }
  };

  const handleCopyLink = (comment) => {
    const url = `${window.location.origin}${window.location.pathname}?comment=${comment.id}`;
    navigator.clipboard.writeText(url).then(() => {
      alert('Link copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
  };

  // Search users for @mentions
  const searchMentionUsers = async (query) => {
    if (!query || query.length < 2) {
      setMentionUsers([]);
      return;
    }
    try {
      const res = await api.getPeople({ tenant_id: tenantId, search: query, limit: 5 });
      setMentionUsers(res.data.people || []);
    } catch (err) {
      console.error('Failed to search users:', err);
    }
  };

  const handleMentionSelect = (mentionUser) => {
    if (!selectedMentions.find(u => u.id === mentionUser.id)) {
      setSelectedMentions([...selectedMentions, mentionUser]);
    }
    setNewComment(newComment.replace(/@\w*$/, `@${mentionUser.name || mentionUser.email} `));
    setShowMentions(false);
    setMentionSearch('');
    textareaRef.current?.focus();
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    setNewComment(value);

    // Check for @mention trigger
    const match = value.match(/@(\w*)$/);
    if (match && capabilities?.capabilities?.mentions) {
      setShowMentions(true);
      setMentionSearch(match[1]);
      searchMentionUsers(match[1]);
    } else {
      setShowMentions(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const canEdit = (comment) => comment.created_by === user?.id;
  const canPin = capabilities?.user_permissions?.can_pin || false;
  const canAssign = capabilities?.user_permissions?.can_assign || false;
  const canCreate = capabilities?.user_permissions?.can_create !== false; // Default true for backwards compat
  const canMention = capabilities?.capabilities?.mentions;
  const canNotify = capabilities?.capabilities?.notifications;
  const tierLocked = capabilities?.tier === 'starter';

  if (tierLocked) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-700">
          <Crown size={18} />
          <span className="font-medium">Comments require Growth tier or higher</span>
        </div>
        <p className="text-sm text-amber-600 mt-1">
          Upgrade your plan to enable team collaboration through comments.
        </p>
      </div>
    );
  }

  // Role-locked message if user can't create
  if (!canCreate && capabilities?.user_permissions) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-2 text-amber-700">
          <Lock size={18} />
          <span className="font-medium">Comment creation restricted</span>
        </div>
        <p className="text-sm text-amber-600 mt-1">
          Your role does not have permission to create comments. Contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${compact ? 'h-full' : 'min-h-[400px]'}`} data-testid="comments-panel">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <MessageCircle size={18} className="text-[var(--clr-brand-primary)]" />
          <span className="font-medium text-gray-800">Comments</span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {comments.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={handleToggleSubscription} 
            className={`p-1.5 rounded transition-colors ${
              isSubscribed 
                ? 'bg-[var(--clr-brand-primary)]/10 text-[var(--clr-brand-primary)]' 
                : 'hover:bg-gray-100 text-gray-500'
            }`}
            title={isSubscribed ? "Unsubscribe from notifications" : "Subscribe to notifications"}
          >
            {isSubscribed ? <BellRing size={14} /> : <BellOff size={14} />}
          </button>
          <button 
            onClick={loadComments} 
            className="p-1.5 hover:bg-gray-100 rounded"
            title="Refresh comments"
          >
            <RefreshCw size={14} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-2 p-2 bg-red-50 text-red-700 text-sm rounded flex items-center gap-2">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto py-3 space-y-3">
        {loading ? (
          <div className="text-center text-gray-500 py-8">Loading comments...</div>
        ) : comments.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
            <p>No comments yet</p>
            <p className="text-xs">Be the first to add a comment</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div 
              key={comment.id}
              className={`p-3 rounded-lg border ${
                comment.resolved
                  ? 'bg-gray-50 border-gray-200 opacity-75'
                  : comment.pinned 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-white border-gray-200'
              }`}
              data-testid={`comment-${comment.id}`}
            >
              {/* Comment header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {comment.created_by_avatar ? (
                    <img 
                      src={comment.created_by_avatar} 
                      alt="" 
                      className="w-7 h-7 rounded-full"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[var(--clr-brand-primary)] flex items-center justify-center text-white text-xs font-medium">
                      {(comment.created_by_name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-medium text-gray-800">
                        {comment.created_by_name || 'Unknown User'}
                      </span>
                      {comment.created_by_role && (
                        <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded capitalize">
                          {comment.created_by_role.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {comment.resolved && (
                    <CheckCircle size={12} className="text-green-500" title="Resolved" />
                  )}
                  {comment.visibility === 'private' && (
                    <Lock size={12} className="text-gray-400" title="Private comment" />
                  )}
                  {comment.pinned && (
                    <Pin size={12} className="text-amber-600" title="Pinned" />
                  )}
                  
                  {/* Action menu */}
                  {(canEdit(comment) || canPin) && (
                    <div className="relative group">
                      <button className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreVertical size={14} className="text-gray-500" />
                      </button>
                      <div className="absolute right-0 top-6 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block">
                        {/* Copy link */}
                        <button
                          onClick={() => handleCopyLink(comment)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          <Copy size={14} /> Copy link
                        </button>
                        
                        {/* Mark resolved/unresolve */}
                        <button
                          onClick={() => handleToggleResolved(comment)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                        >
                          {comment.resolved ? (
                            <><XCircle size={14} /> Reopen</>
                          ) : (
                            <><CheckCircle size={14} /> Mark resolved</>
                          )}
                        </button>
                        
                        {canEdit(comment) && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => {
                                setEditingId(comment.id);
                                setEditText(comment.message);
                              }}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Edit2 size={14} /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(comment.id)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </>
                        )}
                        {canPin && (
                          <>
                            <button
                              onClick={() => handleTogglePin(comment)}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              {comment.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                              {comment.pinned ? 'Unpin' : 'Pin'}
                            </button>
                          </>
                        )}
                        {canNotify && (
                          <>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => handleNotify(comment.id, 'teams')}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Bell size={14} /> Push to Teams
                            </button>
                            <button
                              onClick={() => handleNotify(comment.id, 'whatsapp')}
                              className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                            >
                              <Bell size={14} /> Push to WhatsApp
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Comment body */}
              {editingId === comment.id ? (
                <div className="mt-2">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleEdit(comment.id)}
                      className="px-3 py-1 bg-[var(--clr-brand-primary)] text-white text-sm rounded hover:opacity-90"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => { setEditingId(null); setEditText(''); }}
                      className="px-3 py-1 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                  {comment.message}
                </p>
              )}

              {/* Mentions */}
              {comment.mentions?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {comment.mentions.map((mentionId, idx) => (
                    <span 
                      key={idx}
                      className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full"
                    >
                      @{mentionId}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Assigned to indicator */}
              {comment.assigned_to && (
                <div className="mt-2 flex items-center gap-1 text-xs text-purple-700 bg-purple-50 px-2 py-1 rounded-full w-fit">
                  <UserPlus size={12} />
                  <span>Assigned to: {comment.assigned_to_name || 'User'}</span>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* New comment form */}
      <div className="pt-3 border-t border-gray-200">
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={newComment}
              onChange={handleTextChange}
              placeholder={canMention ? "Add a comment... (use @ to mention)" : "Add a comment..."}
              className="w-full p-3 pr-10 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-transparent"
              rows={2}
              data-testid="comment-input"
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="absolute right-2 bottom-2 p-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="comment-submit"
            >
              <Send size={16} />
            </button>
          </div>

          {/* @mention dropdown */}
          {showMentions && mentionUsers.length > 0 && (
            <div 
              ref={mentionRef}
              className="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto"
            >
              {mentionUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => handleMentionSelect(u)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2"
                >
                  <div className="w-6 h-6 rounded-full bg-[var(--clr-brand-primary)] flex items-center justify-center text-white text-xs">
                    {(u.name || u.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{u.name || u.email}</div>
                    {u.role && <div className="text-xs text-gray-500">{u.role}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Selected mentions */}
          {selectedMentions.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedMentions.map((u) => (
                <span 
                  key={u.id}
                  className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1"
                >
                  @{u.name || u.email}
                  <button 
                    type="button"
                    onClick={() => setSelectedMentions(selectedMentions.filter(m => m.id !== u.id))}
                    className="hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Visibility and Assign toggles */}
          <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setVisibility('org')}
                className={`flex items-center gap-1 px-2 py-1 rounded ${
                  visibility === 'org' 
                    ? 'bg-[var(--clr-brand-primary)] text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Globe size={12} /> Organization
              </button>
              <button
                type="button"
                onClick={() => setVisibility('private')}
                className={`flex items-center gap-1 px-2 py-1 rounded ${
                  visibility === 'private' 
                    ? 'bg-[var(--clr-brand-primary)] text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Lock size={12} /> Private
              </button>
              
              {/* Assign button - only for users with assign permission */}
              {canAssign && (
                <button
                  type="button"
                  onClick={() => setShowAssignPicker(!showAssignPicker)}
                  className={`flex items-center gap-1 px-2 py-1 rounded ${
                    assignedTo 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <UserPlus size={12} /> 
                  {assignedTo ? `Assigned: ${assignedTo.name}` : 'Assign'}
                </button>
              )}
            </div>

            {/* Tier indicators */}
            <div className="flex items-center gap-2">
              {!canMention && (
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <AtSign size={12} /> @mentions (Enterprise)
                </span>
              )}
            </div>
          </div>
          
          {/* Assigned user display */}
          {assignedTo && (
            <div className="mt-2 flex items-center gap-2 text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded-lg w-fit">
              <UserPlus size={12} />
              <span>Will be assigned to: {assignedTo.name || assignedTo.email}</span>
              <button 
                type="button"
                onClick={() => setAssignedTo(null)}
                className="hover:text-purple-900"
              >
                ×
              </button>
            </div>
          )}
          
          {/* Assign user picker */}
          {showAssignPicker && (
            <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="text"
                placeholder="Search users to assign..."
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                onChange={(e) => {
                  if (e.target.value.length >= 2) {
                    api.getPeople({ tenant_id: tenantId, search: e.target.value, limit: 5 })
                      .then(res => setMentionUsers(res.data.people || []))
                      .catch(() => setMentionUsers([]));
                  }
                }}
              />
              {mentionUsers.length > 0 && (
                <div className="mt-2 space-y-1">
                  {mentionUsers.map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setAssignedTo(u);
                        setShowAssignPicker(false);
                        setMentionUsers([]);
                      }}
                      className="w-full text-left px-2 py-1 text-sm hover:bg-white rounded flex items-center gap-2"
                    >
                      <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs">
                        {(u.name || u.email || 'U').charAt(0).toUpperCase()}
                      </div>
                      {u.name || u.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CommentsPanel;
